import React, { useState, useCallback, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API from '../../api';
import { 
  LayoutDashboard, 
  Users, 
  Package, 
  ShoppingCart, 
  FileText, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  ChevronDown,
  Receipt
} from 'lucide-react';

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showLogoutMessage, setShowLogoutMessage] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false); // ✅ Track logout state
  const navigate = useNavigate();
  const location = useLocation();
  
  const { user, logout: authLogout, loading } = useAuth();

  // Superadmin tenant selection states
  const [pharmacies, setPharmacies] = useState([]);
  const [selectedPharmacyId, setSelectedPharmacyId] = useState(
    localStorage.getItem('selectedTenantId') || ''
  );

  useEffect(() => {
    const fetchPharmacies = async () => {
      try {
        const { data } = await API.get('/users/admin/users');
        setPharmacies(data.filter(u => u.role === 'admin') || []);
      } catch (err) {
        console.error("Failed to load pharmacies", err);
      }
    };
    fetchPharmacies();
  }, []);

  const handlePharmacyChange = (e) => {
    const id = e.target.value;
    setSelectedPharmacyId(id);
    if (id) {
      localStorage.setItem('selectedTenantId', id);
      navigate('/dashboard');
    } else {
      localStorage.removeItem('selectedTenantId');
    }
    window.location.reload();
  };

  const adminName = user?.name || localStorage.getItem('userName') || 'Admin User';
  const adminEmail = user?.email || localStorage.getItem('userEmail') || 'admin@pharmacy.com';

  // ✅ FIXED LOGOUT - Direct navigation to landing page
  const handleLogout = useCallback(async () => {
    
    
    // Set logging out state to prevent any redirects
    setIsLoggingOut(true);
    setUserMenuOpen(false);
    setShowLogoutMessage(true);
    
    // Wait for toast animation (1.2 seconds)
    await new Promise(resolve => setTimeout(resolve, 1200));
    
  
    
    // ✅ Clear all localStorage FIRST
    localStorage.removeItem('token');
    localStorage.removeItem('pendingPayment');
    localStorage.removeItem('userStatus');
    localStorage.removeItem('trialInfo');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('pharmacyName');
    
    setShowLogoutMessage(false);
    
    // ✅ Navigate directly to landing page "/" using window.location
    // This bypasses React Router completely - no flash to login page, no "login failed" toast
   // ✅ FIX: Use hash navigation for Electron
  window.location.hash = '#/';
  window.location.reload();
  
}, [authLogout]);

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', color: '#3b82f6' },
    { path: '/admin/users', icon: Users, label: 'User Management', color: '#8b5cf6' },
    { path: '/admin/user-parties', icon: Package, label: 'User Parties', color: '#10b981' },
    { path: '/admin/user-transactions', icon: Receipt, label: 'User Transactions', color: '#f59e0b' },
    { path: '/admin/contacts', icon: FileText, label: 'Contact Messages', color: '#ef4444' },
    { path: '/admin/landing-page', icon: ShoppingCart, label: 'Landing Page', color: '#f97316' },
    { path: '/admin/subscription-plans', icon: Package, label: 'Subscription Plans', color: '#14b8a6' },
    { path: '/admin/subscriptions', icon: ShoppingCart, label: 'Subscriptions', color: '#6366f1' },
    { path: '/admin/payments', icon: FileText, label: 'Payments', color: '#f43f5e' },
    { path: '/admin/notifications/send', icon: Settings, label: 'Send Notification', color: '#22c55e' },
  ];

  const isActive = (path) => location.pathname === path;

  // Show loading state (but not during logout)
  if (loading && !isLoggingOut) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f7fa',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e2e8f0',
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: '#64748b' }}>Loading...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      height: '100vh', 
      backgroundColor: '#f5f7fa', 
      fontFamily: "'Poppins', sans-serif",
      // ✅ Disable pointer events during logout
      pointerEvents: isLoggingOut ? 'none' : 'auto'
    }}>
      
      {/* ✅ Logout Success Toast - Original Animation */}
      {showLogoutMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'white',
          border: '1px solid #e2e8f0',
          borderLeft: '4px solid #10b981',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          padding: '16px 24px',
          borderRadius: '12px',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          animation: 'slideIn 0.3s ease'
        }}>
          <div style={{
            background: '#d1fae5',
            color: '#059669',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 'bold'
          }}>
            ✓
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '15px' }}>Success</div>
            <div style={{ color: '#64748b', fontSize: '13px' }}>Logging out securely...</div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <div style={{
        width: sidebarOpen ? '280px' : '80px',
        backgroundColor: '#1e293b',
        transition: 'width 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 0,
        height: '100vh',
        boxShadow: '2px 0 10px rgba(0,0,0,0.1)',
        opacity: isLoggingOut ? 0.5 : 1,
        transition: 'opacity 0.3s ease, width 0.3s ease'
      }}>
        {/* Logo Section */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              backgroundColor: '#3b82f6',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              💊
            </div>
            {sidebarOpen && (
              <div>
                <h2 style={{ margin: 0, color: 'white', fontSize: '18px', fontWeight: '700' }}>
                  Pharmacy Admin
                </h2>
                <p style={{ margin: 0, color: '#94a3b8', fontSize: '12px' }}>Management Panel</p>
              </div>
            )}
          </div>
        </div>

        {/* Inspect Pharmacy Dropdown (ERP/CRM/Payroll) */}
        {sidebarOpen && (
          <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <label style={{ display: 'block', fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '700', letterSpacing: '0.5px' }}>
              🔍 Inspect Tenant Store
            </label>
            <select
              value={selectedPharmacyId}
              onChange={handlePharmacyChange}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: '#2d3748',
                color: 'white',
                fontSize: '13px',
                fontWeight: '600',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="" style={{ background: '#1e293b' }}>-- Select Pharmacy --</option>
              {pharmacies.map(p => (
                <option key={p._id} value={p._id} style={{ background: '#1e293b' }}>
                  {p.pharmacyName || p.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Navigation Menu */}
        <nav style={{ flex: 1, padding: '20px 0', overflowY: 'auto' }}>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '14px 24px',
                  color: active ? 'white' : '#94a3b8',
                  textDecoration: 'none',
                  backgroundColor: active ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                  borderLeft: active ? `4px solid ${item.color}` : '4px solid transparent',
                  transition: 'all 0.2s',
                  marginBottom: '4px',
                  pointerEvents: isLoggingOut ? 'none' : 'auto'
                }}
                onMouseEnter={(e) => {
                  if (!active && !isLoggingOut) {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                    e.currentTarget.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#94a3b8';
                  }
                }}
              >
                <Icon size={20} color={active ? item.color : '#94a3b8'} />
                {sidebarOpen && (
                  <span style={{ fontSize: '14px', fontWeight: active ? '600' : '500' }}>
                    {item.label}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User Profile Section */}
        <div style={{
          padding: '20px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          position: 'relative'
        }}>
          <div
            onClick={() => !isLoggingOut && setUserMenuOpen(!userMenuOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: isLoggingOut ? 'not-allowed' : 'pointer',
              padding: '10px',
              borderRadius: '8px',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => !isLoggingOut && (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)')}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: '600',
              color: 'white'
            }}>
              {adminName.charAt(0).toUpperCase()}
            </div>
            {sidebarOpen && (
              <>
                <div style={{ flex: 1 }}>
                  <div style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>
                    {adminName}
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '12px' }}>Administrator</div>
                </div>
                <ChevronDown 
                  size={16} 
                  color="#94a3b8" 
                  style={{
                    transition: 'transform 0.2s',
                    transform: userMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                />
              </>
            )}
          </div>

          {/* User Menu Dropdown */}
          {userMenuOpen && sidebarOpen && !isLoggingOut && (
            <div style={{
              position: 'absolute',
              bottom: '100%',
              left: '20px',
              right: '20px',
              backgroundColor: '#2d3748',
              borderRadius: '8px',
              padding: '8px',
              marginBottom: '10px',
              boxShadow: '0 -4px 12px rgba(0,0,0,0.3)',
              animation: 'fadeIn 0.2s ease'
            }}>
              <Link
                to="/admin/profile"
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  textDecoration: 'none',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(148, 163, 184, 0.1)';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#94a3b8';
                }}
                onClick={() => setUserMenuOpen(false)}
              >
                <Users size={18} />
                Profile
              </Link>

              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: isLoggingOut ? '#94a3b8' : '#f87171',
                  cursor: isLoggingOut ? 'not-allowed' : 'pointer',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background 0.2s',
                  opacity: isLoggingOut ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isLoggingOut) {
                    e.currentTarget.style.backgroundColor = 'rgba(248, 113, 113, 0.1)';
                  }
                }}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <LogOut size={18} />
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </button>
            </div>
          )}
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => !isLoggingOut && setSidebarOpen(!sidebarOpen)}
          disabled={isLoggingOut}
          style={{
            position: 'absolute',
            right: '-12px',
            top: '24px',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: '#3b82f6',
            border: 'none',
            cursor: isLoggingOut ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
            transition: 'transform 0.2s',
            opacity: isLoggingOut ? 0.5 : 1
          }}
          onMouseEnter={(e) => !isLoggingOut && (e.currentTarget.style.transform = 'scale(1.1)')}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          {sidebarOpen ? <X size={14} /> : <Menu size={14} />}
        </button>
      </div>

      {/* Main Content Area */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        overflow: 'hidden',
        opacity: isLoggingOut ? 0.5 : 1,
        transition: 'opacity 0.3s ease'
      }}>
        {/* Top Header */}
        <header style={{
          backgroundColor: 'white',
          padding: '20px 30px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1a202c' }}>
              {menuItems.find(item => isActive(item.path))?.label || 'Admin Panel'}
            </h1>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
              Welcome back, {adminName}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {user && (
              <div style={{
                padding: '8px 16px',
                backgroundColor: user.status === 'active' ? '#dcfce7' : '#fef3c7',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                color: user.status === 'active' ? '#166534' : '#92400e',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: user.status === 'active' ? '#16a34a' : '#f59e0b'
                }}></span>
                {user.status === 'active' ? 'Active' : user.status}
              </div>
            )}
            <div style={{
              padding: '8px 16px',
              backgroundColor: '#eff6ff',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              color: '#1e40af'
            }}>
              👑Super Admin
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main style={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: '#f5f7fa'
        }}>
          <Outlet />
        </main>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translate(-50%, -20px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}